import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../api/auth';
import { STORAGE_KEYS } from '../api/axios';
import type { VendorUser } from '../types';

interface AuthContextValue {
  vendorUser: VendorUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True when the backend requires a password rotation before the
   *  user can do anything else. Routes other than /change-password
   *  redirect to it while this is set. */
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Called after the change-password flow completes successfully.
   *  Clears mustChangePassword + signs the vendor out so they can
   *  log in fresh with the new password. */
  onPasswordChanged: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [vendorUser, setVendorUser] = useState<VendorUser | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.vendorUser);
    const token = localStorage.getItem(STORAGE_KEYS.accessToken);
    if (stored && token) {
      try {
        const parsed = JSON.parse(stored) as VendorUser;
        setVendorUser(parsed);
        setMustChangePassword(parsed.mustChangePassword);
      } catch {
        localStorage.removeItem(STORAGE_KEYS.vendorUser);
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    localStorage.setItem(STORAGE_KEYS.accessToken, response.accessToken);
    localStorage.setItem(STORAGE_KEYS.refreshToken, response.refreshToken);
    localStorage.setItem(
      STORAGE_KEYS.vendorUser,
      JSON.stringify(response.vendorUser),
    );
    setVendorUser(response.vendorUser);
    setMustChangePassword(response.mustChangePassword);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // ignore — clear tokens regardless
      }
    }
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.vendorUser);
    setVendorUser(null);
    setMustChangePassword(false);
  };

  /** After password change, backend revoked all refresh tokens — we
   *  have to log out so the user can re-authenticate with the new
   *  password. UX: kick to login w/ success toast. */
  const onPasswordChanged = async () => {
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.vendorUser);
    setVendorUser(null);
    setMustChangePassword(false);
  };

  return (
    <AuthContext.Provider
      value={{
        vendorUser,
        isAuthenticated: !!vendorUser,
        isLoading,
        mustChangePassword,
        login,
        logout,
        onPasswordChanged,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
